import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { queryClient } from "@/lib/queryClient";
import type { PageIndicator, DocumentUser, IndicatorComment, DocumentPage } from "@shared/schema";

// Comment with user data type
type CommentWithUser = IndicatorComment & { user: DocumentUser };

interface UseMainViewingAreaUtilsProps {
  activeIndicator: PageIndicator | null;
  currentPage: number;
  currentPageId: string | null;
  pages: DocumentPage[];
  onPageChange: (pageId: string, pageNumber: number) => void;
}

export function useMainViewingAreaUtils({
  activeIndicator,
  currentPage,
  currentPageId,
  pages,
  onPageChange,
}: UseMainViewingAreaUtilsProps) {
  
  // Edit comment state
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
  const [editingCommentText, setEditingCommentText] = useState("");

  // Edit comment mutation with optimistic updates
  const editCommentMutation = useMutation({
    mutationFn: async ({ commentId, content }: { commentId: string; content: string }) => {
      const response = await fetch(`/api/comments/${commentId}`, {
        method: "PATCH", 
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ content }),
      });
      if (!response.ok) throw new Error("Failed to edit comment");
      return response.json();
    },
    onMutate: async ({ commentId, content }) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: ["/api/indicators", activeIndicator?.id, "comments"] });
      
      // Snapshot previous value
      const previousComments = queryClient.getQueryData<CommentWithUser[]>(["/api/indicators", activeIndicator?.id, "comments"]);
      
      // Optimistically update
      queryClient.setQueryData<CommentWithUser[]>(
        ["/api/indicators", activeIndicator?.id, "comments"],
        (old) => {
          if (!old) return old;
          return old.map(comment => 
            comment.id === commentId 
              ? { ...comment, content }
              : comment
          );
        }
      );
      
      return { previousComments };
    },
    onSuccess: () => {
      // Reset editing state
      setEditingCommentId(null);
      setEditingCommentText("");
    },
    onError: (err, variables, context) => {
      // Rollback on error
      if (context?.previousComments) {
        queryClient.setQueryData(
          ["/api/indicators", activeIndicator?.id, "comments"],
          context.previousComments
        );
      }
      // Reset editing state
      setEditingCommentId(null);
      setEditingCommentText("");
    },
  });

  // Navigation handlers - navigate through VISIBLE pages only, passing visible array positions
  const handlePreviousPage = () => {
    if (!currentPageId) return;
    
    // Get visible pages array (this matches filmstrip display order)
    const visiblePages = pages.filter(p => !p.hidden);
    
    // Find current page position in visible pages array
    const currentVisibleIndex = visiblePages.findIndex(p => p.id === currentPageId);
    if (currentVisibleIndex === -1) return; // Current page not found
    
    // Navigate to previous visible page if exists
    if (currentVisibleIndex > 0) {
      const prevPage = visiblePages[currentVisibleIndex - 1];
      // Pass position in visible pages array (1-based) - this matches URL expectations
      onPageChange(prevPage.id, currentVisibleIndex);
    }
  };

  const handleNextPage = () => {
    if (!currentPageId) return;
    
    // Get visible pages array (this matches filmstrip display order)
    const visiblePages = pages.filter(p => !p.hidden);
    
    // Find current page position in visible pages array
    const currentVisibleIndex = visiblePages.findIndex(p => p.id === currentPageId);
    if (currentVisibleIndex === -1) return; // Current page not found
    
    // Navigate to next visible page if exists
    if (currentVisibleIndex < visiblePages.length - 1) {
      const nextPage = visiblePages[currentVisibleIndex + 1];
      // Pass position in visible pages array (1-based) - this matches URL expectations
      onPageChange(nextPage.id, currentVisibleIndex + 2);
    }
  };

  // Status utility functions
  const getStatusColor = (status: string) => {
    switch (status) {
      case "completed": return "text-green-600 dark:text-green-400";
      case "processing": return "text-yellow-600 dark:text-yellow-400";
      case "failed": return "text-red-600 dark:text-red-400";
      default: return "text-gray-600 dark:text-gray-400";
    }
  };

  const getStatusText = (status: string, progress?: number | null) => {
    switch (status) {
      case "completed": return "Processed";
      case "processing": {
        // Only show percentage if progress is a valid finite number
        if (progress !== undefined && progress !== null && Number.isFinite(progress)) {
          const rounded = Math.round(progress);
          const clamped = Math.max(0, Math.min(100, rounded)); // Clamp to 0-100
          return `Processing ${clamped}%`;
        }
        return "Processing";
      }
      case "failed": return "Failed";
      default: return "Pending";
    }
  };

  // Helper function to format relative time
  const formatRelativeTime = (date: Date | string) => {
    const uploadDate = new Date(date);
    const now = new Date();
    const diffInHours = Math.floor((now.getTime() - uploadDate.getTime()) / (1000 * 60 * 60));
    const diffInDays = Math.floor(diffInHours / 24);
    
    if (diffInHours < 1) {
      return "just now";
    } else if (diffInHours < 24) {
      return `${diffInHours}h ago`;
    } else if (diffInDays < 7) {
      return `${diffInDays}d ago`;
    } else {
      return uploadDate.toLocaleDateString([], { month: 'short', day: 'numeric' });
    }
  };

  return {
    // Comment editing state
    editingCommentId,
    setEditingCommentId,
    editingCommentText,
    setEditingCommentText,
    
    // Mutations
    editCommentMutation,
    
    // Navigation handlers
    handlePreviousPage,
    handleNextPage,
    
    // Utility functions
    getStatusColor,
    getStatusText,
    formatRelativeTime,
  };
}